import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  Skeleton,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { api } from "../utils/api";

export default function CommentsWidget({ entityType, entityId, title = "Comments" }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [text, setText] = useState("");
  const [msg, setMsg] = useState(null);

  const load = async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/comments/list.php?entity_type=${entityType}&entity_id=${entityId}`,
      );
      setComments(res.comments || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [entityType, entityId]);

  const submit = async () => {
    if (!text.trim()) return;
    setPosting(true);
    setMsg(null);
    try {
      await api.post("/comments/create.php", {
        entity_type: entityType,
        entity_id: entityId,
        body: text.trim(),
      });
      setText("");
      load();
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Failed to post" });
    } finally {
      setPosting(false);
    }
  };

  const initials = (name) =>
    (name || "?")
      .split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <ChatBubbleOutlineIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({comments.length})
        </Typography>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Add a comment…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit();
          }}
        />
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={submit}
          disabled={posting || !text.trim()}
        >
          Post
        </Button>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {loading && (
        <Stack spacing={1}>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} height={60} variant="rectangular" />
          ))}
        </Stack>
      )}

      {!loading && comments.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 3 }}
        >
          No comments yet. Be the first to comment.
        </Typography>
      )}

      {!loading &&
        comments.map((c) => (
          <Box
            key={c.id}
            sx={{
              display: "flex",
              gap: 1.5,
              mb: 2,
              pb: 2,
              borderBottom: 1,
              borderColor: "divider",
              "&:last-of-type": { borderBottom: 0, pb: 0, mb: 0 },
            }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main" }}>
              {initials(c.user_name)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {c.user_name || "Unknown"}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {c.created_at}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {c.body}
              </Typography>
            </Box>
          </Box>
        ))}
    </Paper>
  );
}
